/**
 * State + UI provider for the external-agent guided training tour
 * (MRG-281). Mounted once near the root of the authenticated tree (in
 * [_auth.tsx](../../routes/_auth.tsx)) so:
 *
 * 1. The Welcome modal can render the moment a freshly-onboarded
 *    external agent lands on `/properties`.
 * 2. Driver.js spotlights run through the five numbered tour stops and
 *    the two `/properties/create` pop-overs without leaking state into
 *    the route components.
 * 3. PropertyForm and the create page can call `requestCompletion(...)`
 *    from anywhere to bring up the right completion modal once the
 *    agent submits, saves a draft, or exits.
 *
 * Mirrors the investor product tour
 * (see [useProductTour.ts](../product-tour/useProductTour.ts)) but uses
 * a state machine instead of an auto-walk because the tour pauses on
 * the submit page and waits for a real user action to finish.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { driver, type DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'
import './agentTrainingTour.css'

import { useUserProfile } from '@/common/hooks/useUserProfile'
import { CompletionModal, type CompletionType } from './CompletionModal'
import { WelcomeModal } from './WelcomeModal'
import { useAgentTrainingMode } from './useAgentTrainingMode'
import {
  type AgentTrainingPhaseId,
  PHASE_ORDER,
  getNextPhase,
  getPreviousPhase,
  resolvePhaseStep,
} from './agentTrainingPhases'

// =============================================================================
// State + context
// =============================================================================

type Stage = 'idle' | 'welcome' | 'tour' | 'free' | 'completing'

interface AgentTrainingTourContextValue {
  /** True iff this user is an in-training external agent. */
  isTrainingMode: boolean
  /**
   * The phase the driver.js spotlight is currently driving, or `null`
   * when the welcome / completion modals are showing or no tour stage
   * is active. Consumers (e.g. the first offer card on `/offers`) read
   * this to programmatically open UI that the spotlight points at —
   * Step 4's Figma frame (54:383) shows the offer's action menu open
   * with Engage / Accept / Reject visible.
   */
  currentPhase: AgentTrainingPhaseId | null
  /**
   * Advance the tour from the submit-intro sticky note to the
   * submit-actions popover. Called by `SubmitPageGuide`'s Next button
   * because submit-* phases are rendered as React components, not
   * driver.js spotlights.
   */
  advanceFromSubmitIntro: () => void
  /** Trigger one of the three completion popups (called by PropertyForm + create.tsx). */
  requestCompletion: (type: CompletionType) => void
}

const AgentTrainingTourContext = createContext<AgentTrainingTourContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export function useAgentTrainingTourContext(): AgentTrainingTourContextValue {
  const ctx = useContext(AgentTrainingTourContext)
  if (!ctx) {
    // No-op fallback so components in tests / outside the provider don't crash.
    return {
      isTrainingMode: false,
      currentPhase: null,
      advanceFromSubmitIntro: () => {},
      requestCompletion: () => {},
    }
  }
  return ctx
}

// =============================================================================
// Helpers
// =============================================================================

function waitForElement(selector: string, timeoutMs = 5000): Promise<Element | null> {
  return new Promise((resolve) => {
    const el = document.querySelector(selector)
    if (el) return resolve(el)

    const start = Date.now()
    const interval = setInterval(() => {
      const found = document.querySelector(selector)
      if (found) {
        clearInterval(interval)
        resolve(found)
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval)
        resolve(null)
      }
    }, 100)
  })
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// =============================================================================
// Provider
// =============================================================================

export function AgentTrainingTourProvider({ children }: { children: ReactNode }) {
  const isTrainingMode = useAgentTrainingMode()
  const { completeTraining } = useUserProfile()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [stage, setStage] = useState<Stage>('idle')
  const [completion, setCompletion] = useState<CompletionType | null>(null)
  // Tracks the active driver.js phase so consumers (OfferCard) can
  // mirror UI state — see context value `currentPhase`.
  const [currentPhase, setCurrentPhase] = useState<AgentTrainingPhaseId | null>(null)

  const driverRef = useRef<ReturnType<typeof driver> | null>(null)
  const isTransitioningRef = useRef(false)
  const completionStartedRef = useRef(false)

  // ---------------------------------------------------------------------------
  // Phase routing — what URL each phase needs and what selector to wait for
  // ---------------------------------------------------------------------------
  const setupPhase = useCallback(
    async (phase: AgentTrainingPhaseId): Promise<string | null> => {
      // Each phase has a target route + a settle delay. We always resolve
      // the actual wait selector via `resolvePhaseStep` at the end so the
      // mobile re-anchoring (steps 1-3 swing onto the hamburger button on
      // narrow viewports) stays in one place.
      switch (phase) {
        case 'step-1-listings':
          if (window.location.pathname !== '/properties') {
            navigate({ to: '/properties' })
            await delay(600)
          }
          break

        case 'step-2-offers-intro':
        case 'step-3-offers-email':
        case 'step-4-offers-actions':
        case 'step-5-next-submit':
          // Steps 2-5 all live on the global agent offers page (`/offers`).
          // The fake offers come from the API's training_mode short-circuit;
          // the navbar Submit button (step 5) is also visible there.
          if (window.location.pathname !== '/offers') {
            navigate({ to: '/offers' })
            await delay(600)
          }
          break

        case 'submit-intro':
          if (window.location.pathname !== '/properties/create') {
            navigate({ to: '/properties/create' })
            await delay(700)
          }
          // Always land the agent at the top of the form — the auto-
          // focus on the first input can shift scrollY mid-page,
          // which gives the illusion that the page "starts" scrolled
          // and breaks the scroll-driven dismiss / advance flow.
          window.scrollTo({ top: 0, behavior: 'auto' })
          break

        case 'submit-actions':
          // Same page as submit-intro — scroll to bottom so the Submit
          // button enters the viewport.
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
          await delay(400)
          break

        default:
          return null
      }

      const element = resolvePhaseStep(phase).element
      return typeof element === 'string' ? element : null
    },
    [navigate],
  )

  // ---------------------------------------------------------------------------
  // Drive a single phase via driver.js — Back/Next handle phase transitions
  // ---------------------------------------------------------------------------
  const startPhase = useCallback(
    async (phase: AgentTrainingPhaseId) => {
      if (isTransitioningRef.current) return
      isTransitioningRef.current = true

      if (driverRef.current) {
        driverRef.current.destroy()
        driverRef.current = null
      }

      // Publish the phase early so dependent UI (e.g. the first offer
      // card on `/offers`) can react and open its dropdown menu before
      // the driver.js spotlight needs to find it.
      setCurrentPhase(phase)

      // Submit-page phases are rendered as a React component
      // (`SubmitPageGuide`), NOT a driver.js spotlight — the Figma
      // calls for a free-floating sticky note that doesn't dim the
      // surrounding form. Just navigate + leave currentPhase published
      // and the page-level component will pick up the rendering.
      if (phase === 'submit-intro' || phase === 'submit-actions') {
        try {
          await setupPhase(phase)
        } catch (err) {
          console.error(`[AgentTrainingTour] setupPhase("${phase}") failed:`, err)
          isTransitioningRef.current = false
          setStage('free')
          setCurrentPhase(null)
          return
        }
        isTransitioningRef.current = false
        return
      }

      let waitSelector: string | null = null
      try {
        waitSelector = await setupPhase(phase)
      } catch (err) {
        console.error(`[AgentTrainingTour] setupPhase("${phase}") failed:`, err)
        isTransitioningRef.current = false
        setStage('free')
        setCurrentPhase(null)
        return
      }
      if (waitSelector) {
        const el = await waitForElement(waitSelector, 5000)
        if (!el) {
          console.warn(
            `[AgentTrainingTour] target "${waitSelector}" missing for phase "${phase}", releasing tour`,
          )
          isTransitioningRef.current = false
          setStage('free')
          setCurrentPhase(null)
          return
        }
      }
      await delay(150)

      const step = resolvePhaseStep(phase)
      const isFirstPhase = phase === PHASE_ORDER[0]
      const isLastPhase = phase === PHASE_ORDER[PHASE_ORDER.length - 1]
      // Each phase runs as a single-step driver instance, which means
      // driver.js's `hasPreviousStep()` returns false and it auto-injects
      // `disableButtons: ["previous"]` — disabling the Back button so our
      // `onPrevClick` callback never fires. We pass `disableButtons: []`
      // explicitly to override that default; phase transitions are handled
      // in `onPrevClick` / `onDestroyStarted` below.
      const stepWithButtons: DriveStep = {
        ...step,
        popover: {
          ...step.popover,
          disableButtons: [],
          ...(isFirstPhase
            ? { showButtons: ['next' as const] }
            : isLastPhase
              ? { showButtons: ['previous' as const, 'next' as const], doneBtnText: 'Got it' }
              : { showButtons: ['previous' as const, 'next' as const] }),
        },
      }

      let destroyHandled = false
      const driverInstance = driver({
        showProgress: false,
        steps: [stepWithButtons],
        nextBtnText: 'Next →',
        prevBtnText: 'Back',
        doneBtnText: isLastPhase ? 'Got it' : 'Next →',
        allowClose: false,
        overlayClickBehavior: 'close',
        // Desktop: the agent needs to see the surrounding content as
        // part of the training (Figma flow), so the overlay is fully
        // transparent and only `disableActiveInteraction` below blocks
        // stray clicks. Mobile: per the Anthropic-Claude-Design chat1
        // SpotlightCutout, we restore a dark scrim with a rounded-rect
        // cutout around the highlighted element — narrow viewports
        // need the contrast to telegraph WHERE the popover is pointing.
        overlayColor:
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(max-width: 1023px)').matches
            ? 'rgba(15, 12, 8, 0.62)'
            : 'rgba(0, 0, 0, 0)',
        disableActiveInteraction: true,
        stagePadding: 4,
        stageRadius: 8,
        popoverOffset: 16,
        smoothScroll: true,
        popoverClass: 'agent-training-tour-popover',
        onPrevClick: () => {
          if (destroyHandled) return
          destroyHandled = true
          driverInstance.destroy()
          driverRef.current = null
          isTransitioningRef.current = false
          const prev = getPreviousPhase(phase)
          if (prev) {
            startPhase(prev)
          } else {
            setCurrentPhase(null)
            setStage('welcome')
          }
        },
        onDestroyStarted: () => {
          if (destroyHandled) return
          destroyHandled = true
          driverInstance.destroy()
          driverRef.current = null
          isTransitioningRef.current = false
          const next = getNextPhase(phase)
          if (next) {
            startPhase(next)
          } else {
            setCurrentPhase(null)
            setStage('free')
          }
        },
      })

      driverRef.current = driverInstance
      driverInstance.drive()
    },
    [setupPhase],
  )

  // Stable ref so the auto-launch effect doesn't re-fire when navigate changes
  const startPhaseRef = useRef(startPhase)
  useLayoutEffect(() => {
    startPhaseRef.current = startPhase
  }, [startPhase])

  // ---------------------------------------------------------------------------
  // Auto-launch the welcome modal when a fresh in-training agent appears
  // ---------------------------------------------------------------------------
  const hasLaunchedRef = useRef(false)
  useEffect(() => {
    if (!isTrainingMode) {
      hasLaunchedRef.current = false
      return
    }
    if (hasLaunchedRef.current) return
    hasLaunchedRef.current = true
    setStage('welcome')
  }, [isTrainingMode])

  // ---------------------------------------------------------------------------
  // Mobile bottom-sheet anchor + auto-scroll. The Anthropic-Claude-Design
  // handoff (Mobile Guided Training.html) replaces the cramped floating
  // popover with a thumb-zone bottom sheet. We keep the existing driver.js
  // flow but, on narrow viewports, (a) auto-scroll the highlighted target
  // into the upper third of the viewport so the sheet doesn't cover it,
  // and (b) flip the sheet to the top of the screen when the target is in
  // the lower half. The CSS in `agentTrainingTour.css` reads
  // `body.agent-tour-anchor-top` to swap from bottom-sheet to top-sheet.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (typeof document === 'undefined') return
    const isMobile = window.matchMedia('(max-width: 1023px)').matches
    if (!isMobile) {
      document.body.classList.remove('agent-tour-anchor-top')
      return
    }
    if (!currentPhase || !PHASE_ORDER.includes(currentPhase)) {
      document.body.classList.remove('agent-tour-anchor-top')
      return
    }
    // Submit-* phases render fixed-position sticky notes / centered
    // modals — measuring their rect and scrolling toward them is
    // meaningless and competes with `setupPhase`'s top-of-page reset.
    if (currentPhase === 'submit-intro' || currentPhase === 'submit-actions') {
      document.body.classList.remove('agent-tour-anchor-top')
      return
    }
    // Wait for navigation + driver.js render to settle. setupPhase already
    // delays ~600ms after route change before driving the spotlight, so the
    // target node should exist in the DOM by the time we measure.
    const timeoutId = window.setTimeout(() => {
      const step = resolvePhaseStep(currentPhase)
      const selector = typeof step.element === 'string' ? step.element : null
      const target = selector ? document.querySelector<HTMLElement>(selector) : null
      if (!target) {
        document.body.classList.remove('agent-tour-anchor-top')
        return
      }
      // Auto-scroll target into the upper third so the bottom sheet has
      // breathing room. Skip when the element already sits in the upper
      // 40% — driver.js's `smoothScroll` handles those.
      const initialRect = target.getBoundingClientRect()
      const upperThird = window.innerHeight * 0.33
      if (initialRect.top > upperThird) {
        const desiredTop = window.scrollY + initialRect.top - upperThird
        window.scrollTo({ top: Math.max(0, desiredTop), behavior: 'smooth' })
      }
      // After the scroll settles, decide which side the sheet anchors to.
      const decideAnchor = () => {
        const rect = target.getBoundingClientRect()
        const middle = rect.top + rect.height / 2
        const flipToTop = middle > window.innerHeight * 0.55
        document.body.classList.toggle('agent-tour-anchor-top', flipToTop)
      }
      window.setTimeout(decideAnchor, 360)
    }, 700)
    return () => {
      window.clearTimeout(timeoutId)
      document.body.classList.remove('agent-tour-anchor-top')
    }
  }, [currentPhase])

  // ---------------------------------------------------------------------------
  // External trigger — PropertyForm or create.tsx asks us to finish training
  // ---------------------------------------------------------------------------
  const requestCompletion = useCallback(
    (type: CompletionType) => {
      // Tear down any active spotlight so it doesn't fight the modal.
      if (driverRef.current) {
        driverRef.current.destroy()
        driverRef.current = null
      }
      setCurrentPhase(null)
      setCompletion(type)
      setStage('completing')

      // Mark training complete on the server so the API gate flips for any
      // subsequent action. Idempotent guard prevents double-calls. Once
      // the timestamp lands, evict any property / offer entries from the
      // React Query cache: those rows came from the training-mode short
      // circuit (fake fixtures), and the next read should fetch real data
      // without the now-rejected `training_mode` flag. Without the
      // eviction, the user could see fake property cards immediately
      // after completion and clicking through them would 404 against the
      // real API.
      if (!completionStartedRef.current) {
        completionStartedRef.current = true
        completeTraining()
          .then(() => {
            queryClient.removeQueries({ queryKey: ['property'] })
            queryClient.removeQueries({ queryKey: ['dataSource', 'properties'] })
            queryClient.removeQueries({ queryKey: ['dataSource', 'offers'] })
          })
          .catch((err) => {
            console.error('[AgentTrainingTour] Failed to mark training complete:', err)
          })
      }
    },
    [completeTraining, queryClient],
  )

  const handleCompletionClose = useCallback(() => {
    setCompletion(null)
    setStage('idle')
    // No navigation here — the agent is wherever they landed after their
    // submit / draft / exit action, which is exactly where they should be.
  }, [])

  const handleWelcomeContinue = useCallback(() => {
    setStage('tour')
    // Defer the spotlight launch one tick so the welcome modal can finish
    // its close animation before driver.js paints the overlay.
    setTimeout(() => startPhaseRef.current(PHASE_ORDER[0]), 100)
  }, [])

  // Bridge from the React-rendered `SubmitPageGuide` Next button into
  // our state machine — submit-intro → submit-actions.
  const advanceFromSubmitIntro = useCallback(() => {
    startPhaseRef.current('submit-actions')
  }, [])

  // Cleanup any lingering driver instance on unmount.
  useEffect(() => {
    return () => {
      if (driverRef.current) driverRef.current.destroy()
    }
  }, [])

  const value: AgentTrainingTourContextValue = {
    isTrainingMode,
    currentPhase,
    advanceFromSubmitIntro,
    requestCompletion,
  }

  return (
    <AgentTrainingTourContext.Provider value={value}>
      {children}
      <WelcomeModal open={stage === 'welcome'} onContinue={handleWelcomeContinue} />
      {completion !== null && (
        <CompletionModal open type={completion} onClose={handleCompletionClose} />
      )}
    </AgentTrainingTourContext.Provider>
  )
}
